import React, { Fragment, Suspense, useEffect } from 'react';
import { Routes as RoutesSwitch, Route } from 'react-router-dom';
import { styled } from '@mui/material/styles';

import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import LogoImage from '../Assets/Images/menu_icon.svg';

// import Topbar from 'Components/Commons/Topbar/Topbar';
import Sidebar from 'Components/Commons/Sidebar/Sidebar';
import Topbar from 'Components/Commons/Topbar/Topbar';
// COMPONENTS
import Login from 'Components/Pages/Login/Login';
import ForgotPassword from 'Components/Pages/ForgotPassword/ForgotPassword';
import ResetPassword from 'Components/Pages/ResetPassword/ResetPassword';
import PageNotFound from 'Components/pageNotFound';

// ROUTING COMPONENTS
import ProtectedRoute from './ProtectedRoute';
import RoutesList from './RoutesList';
import { preloadAllRouteChunks } from './RoutesList';
import { URL_LOGIN, URL_FORGOT_PASSWORD, URL_RESET_PASSWORD } from 'Helpers/Paths';
import CmsLogo from '../Assets/Images/cms-logo.svg';
import useWatchRedirection from 'Hooks/useWatchRedirection';

/**
 * @constant
 * @description Width of the navigation drawer in pixels.
 *
 * @type {number}
 * @default 280
 */
const drawerWidth = 280;

/**
 * @constant
 * @description Styled Material-UI AppBar component with responsive behavior.
 * Adjusts width and margin based on drawer open/closed state.
 *
 * @type {React.Component}
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the drawer is open
 *
 * @example
 * <AppBar open={true} position="fixed">
 *   <Toolbar>Content</Toolbar>
 * </AppBar>
 */
const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    })
}));

/**
 * @constant
 * @description Styled Material-UI Drawer component with collapsible behavior.
 * Provides smooth transitions between expanded and collapsed states.
 *
 * @type {React.Component}
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the drawer is open
 *
 * @example
 * <Drawer variant="permanent" open={open}>
 *   <Sidebar />
 * </Drawer>
 */
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        '& .MuiDrawer-paper': {
            zIndex: theme.zIndex.drawer + 1,
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen
                }),
                width: theme.spacing(7),
                [theme.breakpoints.up('sm')]: {
                    width: theme.spacing(9)
                }
            })
        }
    })
);

/**
 * @component
 * @description Main routing component that handles application navigation and layout.
 * Provides a responsive admin interface with collapsible sidebar, top navigation,
 * and content area. Handles both public routes (login, forgot password) and
 * protected routes with authentication.
 *
 * Features:
 * - Responsive drawer navigation
 * - Protected route handling
 * - Lazy loading with suspense
 * - Authentication flow management
 * - Permission-based redirection
 *
 * @returns {JSX.Element} Complete application routing structure with layout
 *
 * @example
 * // Used in App.js
 * import Routes from './Routes/Route';
 *
 * function App() {
 *   return (
 *     <BrowserRouter>
 *       <Routes />
 *     </BrowserRouter>
 *   );
 * }
 *
 * @see ProtectedRoute - Route wrapper for authentication
 * @see RoutesList - Configuration of protected routes
 * @see useWatchRedirection - Permission checking hook
 */
function Routes() {
    const [open, setOpen] = React.useState(true);

    useWatchRedirection();

    useEffect(() => {
        const schedulePreload = () => preloadAllRouteChunks();

        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
            const id = window.requestIdleCallback(schedulePreload, { timeout: 1200 });
            return () => window.cancelIdleCallback(id);
        }

        const timerId = window.setTimeout(schedulePreload, 300);
        return () => window.clearTimeout(timerId);
    }, []);

    /**
     * @function
     * @description Toggles the drawer open/closed state.
     *
     * @returns {void}
     */
    const toggleDrawer = () => {
        setOpen(!open);
    };

    /**
     * @function
     * @description Renders the complete layout structure for protected routes.
     * Includes AppBar, Drawer, Sidebar, and main content area with lazy loading.
     *
     * @param {Object} route - Route configuration object
     * @param {React.Component} route.component - Component to render in content area
     * @returns {JSX.Element} Complete layout structure
     *
     * @example
     * const route = {
     *   path: '/dashboard',
     *   component: Dashboard
     * };
     * renderRouteContent(route);
     */
    const renderRouteContent = (route) => (
        <Box sx={{ display: 'flex', background: '#002047' }}>
            <AppBar style={{ background: '#002047' }} position="fixed" open={open}>
                <Toolbar sx={{ pr: '24px', display: 'flex', justifyContent: 'space-between' }}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggleDrawer}
                        sx={{
                            color: '#e0eeff',
                            fontSize: '10px',
                            ...(!open && { marginLeft: '60px' })
                        }}>
                        <MenuIcon />
                    </IconButton>
                    <IconButton style={{ float: 'left' }}>
                        <Topbar />
                    </IconButton>
                </Toolbar>
            </AppBar>
            <Drawer variant="permanent" open={open}>
                <Toolbar
                    sx={{
                        position: 'fixed',
                        width: 'inherit',
                        top: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#002047',
                        zIndex: 1,
                        color: '#333333',
                        px: [1]
                    }}>
                    {open && (
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                marginRight: 40
                            }}>
                            <img
                                src={CmsLogo}
                                alt="CMS Logo"
                                loading="lazy"
                                decoding="async"
                                style={{ width: '100px', objectFit: 'contain' }}
                            />
                        </div>
                    )}
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggleDrawer}
                        style={{ color: '#333333' }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ...(open && { display: 'none' }),
                            objectFit: 'contain'
                        }}>
                        <img
                            height={'50px'}
                            alt="profile-user"
                            src={LogoImage}
                            loading="lazy"
                            decoding="async"
                        />
                    </IconButton>
                </Toolbar>
                <Divider sx={{ mb: 7 }} />
                <Sidebar open={open} />
            </Drawer>
            <Suspense fallback={null}>
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        height: '100vh',
                        width: '100%',
                        overflow: 'auto',
                        padding: '20px'
                    }}>
                    <Toolbar />
                    <route.component />
                </Box>
            </Suspense>
        </Box>
    );

    return (
        <RoutesSwitch>
            <Route exact path={'/'} element={<Login />} />
            <Route exact path={URL_LOGIN} element={<Login />} />
            <Route exact path={URL_FORGOT_PASSWORD} element={<ForgotPassword />} />
            <Route exact path={URL_RESET_PASSWORD} element={<ResetPassword />} />
            <Route exact path="*" element={<PageNotFound />} />

            {RoutesList.map((route, index) => (
                <Fragment key={index}>
                    <Route
                        path={route.path}
                        element={<ProtectedRoute>{renderRouteContent(route)}</ProtectedRoute>}
                    />
                </Fragment>
            ))}
        </RoutesSwitch>
    );
}

export default Routes;
